'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { RefreshCw } from 'lucide-react';

import { DataTable } from '@/components/data-table/data-table';
import { useDataTable } from '@/hooks/use-data-table';
import { DataTableSearch } from '@/components/data-table/data-table-search';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { MainTableState } from '@/lib/utils/table-state-utils';
import type { TableConfig } from '@/types/data-table';
import { DEFAULT_SKELETON_ROWS } from '@/config/data-table';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { getExternalRequestsTableColumns, ExternalRequestLog } from './external-requests-table-columns';
import { ExternalRequestsTableSkeleton } from './ExternalRequestsTableSkeleton';

interface ExternalRequestsTableProps {
  logs: ExternalRequestLog[];
  total: number;
  pageCount: number;
  options: {
    service: string[];
    eventType: string[];
    requestType: string[];
    status: string[];
    storeId: { id: string; name: string }[];
  };
  isLoading: boolean;
  isFetching: boolean;
  autoReload: boolean;
  onAutoReloadChange: (enabled: boolean) => void;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  onTableStateChange: (state: MainTableState) => void;
  onRefetch: () => void;
  config?: TableConfig;
}

/**
 * ExternalRequestsTable component for displaying external request logs
 */
export function ExternalRequestsTable({
  logs,
  total,
  pageCount,
  options,
  isLoading,
  isFetching,
  autoReload,
  onAutoReloadChange,
  searchQuery = '',
  onSearchChange,
  onTableStateChange,
  onRefetch,
  config = {},
}: ExternalRequestsTableProps) {
  const { t, i18n } = useTranslation('externalRequests');
  const navigate = useNavigate();
  const { orgRoutes } = useOrgNavigate();

  const handleRowClick = React.useCallback(
    (log: ExternalRequestLog) => {
      navigate(orgRoutes.EXTERNAL_REQUEST_DETAILS(log.id));
    },
    [navigate, orgRoutes],
  );

  // Track if options have ever loaded
  const optionsCount =
    (options?.service?.length || 0) + (options?.eventType?.length || 0) + (options?.storeId?.length || 0);
  const hasLoadedOptionsRef = React.useRef(false);
  if (!hasLoadedOptionsRef.current && optionsCount > 0) {
    hasLoadedOptionsRef.current = true;
  }

  const columns = React.useMemo(
    () =>
      getExternalRequestsTableColumns({
        t,
        options,
      }),
    [t, options, i18n.language],
  );

  const tableKey = `${hasLoadedOptionsRef.current ? 'loaded' : 'loading'}-${i18n.language}`;

  const { table } = useDataTable({
    data: logs,
    columns,
    total,
    config,
    onTableStateChange,
    getRowId: (row: ExternalRequestLog) => row.id,
  });

  return (
    <DataTable
      key={tableKey}
      table={table}
      isLoading={isLoading}
      skeletonRows={<ExternalRequestsTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
      onRefresh={onRefetch}
      onRowClick={handleRowClick}
      actionBar={
        <div className="flex items-center gap-2">
          <Button
            variant={autoReload ? 'default' : 'outline'}
            size="sm"
            onClick={() => onAutoReloadChange(!autoReload)}
            className={cn(autoReload && 'animate-pulse')}
          >
            <RefreshCw className={cn('mr-2 h-4 w-4', isFetching && autoReload && 'animate-spin')} />
            {autoReload ? 'Auto-reload (5s)' : 'Enable Auto-reload'}
          </Button>
        </div>
      }
      extraFilters={<>
        {onSearchChange && (
          <div className='w-full md:w-[200px]'>
            <DataTableSearch value={searchQuery} onChange={onSearchChange} placeholder={t('searchPlaceholder')} />
          </div>
        )}
      </>}
    >

    </DataTable>
  );
}
